import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { Button, Flex, Text, Heading, Box, Progress } from '@thenexlabs/uikit';
import { useWeb3React } from '@web3-react/core';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'contexts/Localization';
import { ToastDescriptionWithTx } from 'components/Toast';
import useToast from 'hooks/useToast';
import { logError } from 'utils/sentry';
import ConnectWalletButton from 'components/ConnectWalletButton';
import { useXpMerchantContract } from 'hooks/useContract';
import { useApproveUsdcCallback, ApprovalState } from 'hooks/usdc/useApproveUsdcCallback';
import useBuyXP from 'hooks/xp/useBuyXP';
import { useXpMerchantData } from 'state/xpMerchant/hooks';
import useTokenBalance from 'hooks/useTokenBalance';
import tokens from 'config/constants/tokens';
import Dots from 'components/Loader/Dots';

const BASE_USDC_PRICE_PER_XP = 0.999;//0.481;
const XP_BUNDLES_CONFIG = [
  { xp: 2, discount: 0 },
  { xp: 10, discount: 0 },
  { xp: 100, discount: 5 },
  { xp: 1000, discount: 10 },
  { xp: 10000, discount: 20 },
];

const XP_BUNDLES = XP_BUNDLES_CONFIG.map(bundle => ({
  ...bundle,
  price: bundle.xp * BASE_USDC_PRICE_PER_XP * (1 - bundle.discount / 100),
}));

const maxPriceForProgressBar = XP_BUNDLES[XP_BUNDLES.length - 1].price;

const BuyXpContainer = styled(Flex)`
  width: 100%;
  max-width: 960px;
  padding: 32px 24px;
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
`;

const BundleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 24px;
  width: 100%;
`;

const StyledBundleCard = styled(Box)<{ isSelected: boolean; hasError: boolean }>`
  position: relative;
  padding: 24px 16px;
  border: 2px solid;
  border-radius: 16px;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s ease-in-out;
  
  /* Conditional Styling Logic */
  border-color: ${({ isSelected, hasError, theme }) => {
    if (hasError) return theme.colors.failure;
    if (isSelected) return 'lime';
    return theme.colors.borderColor;
  }};
  
  box-shadow: ${({ isSelected, hasError, theme }) => {
    if (hasError) return `0px 0px 15px ${theme.colors.failure}`;
    if (isSelected) return '0px 0px 15px rgba(127, 255, 0, 0.5)';
    return 'none';
  }};

  transform: ${({ isSelected }) => (isSelected ? 'translateY(-5px)' : 'none')};

  &:hover {
    transform: translateY(-5px);
    border-color: ${({ hasError, theme }) => (hasError ? theme.colors.failure : 'lime')};
  }
`;

const BestValueBadge = styled(Box)`
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(45deg, #fdbd01, #ffeb3b);
  color: black;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
`;

const WarningText = styled(Text)`
  color: ${({ theme }) => theme.colors.failure};
  font-weight: bold;
  margin-bottom: 16px;
`;

interface BundleCardProps {
  bundle: { xp: number; discount: number; price: number };
  isSelected: boolean;
  isBestValue: boolean;
  hasError: boolean;
  onClick: () => void;
}

const BundleCard: React.FC<BundleCardProps> = ({ bundle, isSelected, isBestValue, hasError, onClick }) => {
  const { t } = useTranslation();
  return (
    <StyledBundleCard isSelected={isSelected} hasError={hasError} onClick={onClick}>
      {isBestValue && <BestValueBadge>{t('Best Value')}</BestValueBadge>}
      <Text fontSize="32px" color="secondary" bold>
        {bundle.xp.toLocaleString()} XP
      </Text>
      {bundle.discount > 0 ? (
        <Text color="success" bold>
          {t('%discount%% Bonus!', { discount: bundle.discount })}
        </Text>
      ) : (
        <Text color="textSubtle" style={{ visibility: 'hidden' }}>Placeholder</Text>
      )}
      <Text mt="8px" color="textSubtle">
        {t('Price: %price% USDC', { price: bundle.price.toFixed(2) })}
      </Text>
    </StyledBundleCard>
  );
};

const BuyXpAction: React.FC = () => {
  const { account } = useWeb3React();
  const { toastSuccess, toastError } = useToast();
  const { t } = useTranslation();
  const [selectedBundleIndex, setSelectedBundleIndex] = useState<number>(4);
  const [pendingTx, setPendingTx] = useState(false);
  const [hasInsufficientFunds, setHasInsufficientFunds] = useState(false);
  const xpMerchantContract = useXpMerchantContract();
  const xpMerchantAddress = xpMerchantContract.address;

  const { xpBalance: remainingXpInStore } = useXpMerchantData();
  const { balance: usdcBalance } = useTokenBalance(tokens.usdc.address);
  const usdcBalanceAsNumber = new BigNumber(usdcBalance).div(10 ** 6).toNumber();

  const { totalCostWei, totalXpWei, selectedBundle } = useMemo(() => {
    const bundle = XP_BUNDLES[selectedBundleIndex];
    if (!bundle) {
      return { totalCostWei: '0', totalXpWei: '0', selectedBundle: null };
    }
    return {
      totalCostWei: new BigNumber(bundle.price).times(10 ** 6).toFixed(0),
      totalXpWei: new BigNumber(bundle.xp).times(10 ** 18).toFixed(0),
      selectedBundle: bundle,
    };
  }, [selectedBundleIndex]);

  useEffect(() => {
    if (account && selectedBundle) {
      setHasInsufficientFunds(selectedBundle.price > usdcBalanceAsNumber);
    } else {
      setHasInsufficientFunds(false);
    }
  }, [selectedBundle, usdcBalanceAsNumber, account]);

  const { onBuyXP } = useBuyXP(totalCostWei, totalXpWei, account);
  const [approvalState, approve] = useApproveUsdcCallback(totalCostWei, xpMerchantAddress);
  const purchasePowerPercentage = Math.min(100, (usdcBalanceAsNumber / maxPriceForProgressBar) * 100);

  const handleBuyXP = async () => {
    if (!account || !selectedBundle || hasInsufficientFunds) return;
    setPendingTx(true);
    try {
      await onBuyXP(
        (tx) => toastSuccess(`${t('Transaction Submitted')}!`, <ToastDescriptionWithTx txHash={tx.hash} />),
        (receipt) => toastSuccess(`${t('Success')}!`, <ToastDescriptionWithTx txHash={receipt.transactionHash}>{t('Your XP has been added!')}</ToastDescriptionWithTx>),
        (receipt) => toastError(t('Error'), <ToastDescriptionWithTx txHash={receipt.transactionHash}>{t('Transaction failed. Please try again.')}</ToastDescriptionWithTx>),
      );
    } catch (e) {
      toastError(t('Error'), t('An unknown error occurred. Please try again.'));
      logError(e);
    } finally {
      setPendingTx(false);
    }
  };

  const renderActionButton = () => {
    if (!account) {
      return <ConnectWalletButton width="100%" />;
    }
    if (!selectedBundle) {
      return <Button width="100%" disabled>{t('Select a Bundle')}</Button>;
    }
    if (hasInsufficientFunds) {
      return <Button width="100%" disabled variant="danger">{t('Insufficient USDC')}</Button>;
    }
    if (approvalState !== ApprovalState.APPROVED) {
      const isApproving = approvalState === ApprovalState.PENDING;
      return (
        <Button width="100%" disabled={isApproving} onClick={approve}>
          {isApproving ? <Dots>{t('Approving USDC')}</Dots> : t('Approve USDC')}
        </Button>
      );
    }
    return (
      <Button width="100%" variant="primary" disabled={pendingTx} onClick={handleBuyXP}>
        {pendingTx ? <Dots>{t('Processing')}</Dots> : t('Buy for %price% USDC', { price: selectedBundle.price.toFixed(2) })}
      </Button>
    );
  };

  return (
    <BuyXpContainer flexDirection="column" alignItems="center">
      <Heading scale="lg" mb="8px">{t('XP Store')}</Heading>
      <Text color="textSubtle" mb="24px">{t('Limited supply available. Get yours now!')}</Text>
      
      <Box width="100%" mb="24px" minHeight="40px">
        {!remainingXpInStore ? (
          <Flex justifyContent="center" alignItems="center" height="100%">
            <Dots>{t('Loading supply...')}</Dots>
          </Flex>
        ) : (
          <Box>
            <Text fontSize="14px" bold color="textSubtle" textAlign="center">{t('XP Available in Store')}</Text>
            <Heading scale="xl" textAlign="center">{new BigNumber(remainingXpInStore.toString()).div(10**18).toFormat(0)}</Heading>
          </Box>
        )}
      </Box>

      {account && (
        <Box width="100%" mb="32px">
          <Flex justifyContent="space-between" mb="4px">
            <Text fontSize="14px" bold>{t('Your Buying Power')}</Text>
            <Text fontSize="14px" bold color="textSubtle">{usdcBalanceAsNumber.toFixed(2)} USDC</Text>
          </Flex>
          <Progress primaryStep={purchasePowerPercentage} />
          <Flex justifyContent="space-between" mt="4px">
            <Text fontSize="12px" color="textSubtle">0 USDC</Text>
            <Text fontSize="12px" color="textSubtle">{maxPriceForProgressBar.toFixed(2)} USDC</Text>
          </Flex>
        </Box>
      )}

      <BundleGrid>
        {XP_BUNDLES.map((bundle, index) => (
          <BundleCard
            key={bundle.xp}
            bundle={bundle}
            isSelected={selectedBundleIndex === index}
            isBestValue={index === XP_BUNDLES.length - 1}
            hasError={selectedBundleIndex === index && hasInsufficientFunds}
            onClick={() => setSelectedBundleIndex(index)}
          />
        ))}
      </BundleGrid>

      <Box mt="24px" width="100%" maxWidth="320px">
        {hasInsufficientFunds && <WarningText textAlign="center">{t('Insufficient USDC balance')}</WarningText>}
        {renderActionButton()}
      </Box>
    </BuyXpContainer>
  );
};

export default BuyXpAction;